import "./App.css";
import PersonaButton from "./Components/PersonaButton";
import PreloadedQuestions from "./Components/PreloadedQuestions";
import ChatDialog from "./Components/ChatDialog";
import { getConversationId } from "./conversation";
import { sendHeartbeat } from "./heartbeat";
import React, { useState, useEffect } from "react";

const DEFAULT_REST_URL = "/wp-json/cmw/v1/chat";
const WELCOME_KEY = "seenChatWelcome";

// Record the moment the welcome teaser was dismissed/seen (timestamp, ms).
function markWelcomeSeen() {
  try {
    localStorage.setItem(WELCOME_KEY, String(Date.now()));
  } catch (e) {
    /* storage unavailable */
  }
}

// Show the teaser if it has never been dismissed, or if the reset window has
// elapsed since it was. resetMs <= 0 means always show. Legacy "1" flags (no
// timestamp) are treated as expired so they show once more, then start timing.
function shouldShowWelcome(resetMs) {
  try {
    const raw = localStorage.getItem(WELCOME_KEY);
    if (!raw) return true;
    const ts = parseInt(raw, 10);
    if (!ts || Number.isNaN(ts)) return true;
    if (resetMs <= 0) return true;
    return Date.now() - ts >= resetMs;
  } catch (e) {
    return true;
  }
}

// Returns { answer, citations, links, disclaimer }. Tolerates the legacy {response} shape.
async function sendPromptToApi(message, restConfig = {}, internal = false) {
  const restUrl = restConfig.url || DEFAULT_REST_URL;
  const restNonce = restConfig.nonce || "";
  const response = await fetch(restUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-WP-Nonce": restNonce,
    },
    // `internal` requests (e.g. generating follow-up suggestions) are excluded
    // from the server-side chat history log.
    body: JSON.stringify({ message, conversation_id: getConversationId(), internal }),
  });

  const data = await response.json();

  if (!response.ok) {
    const messageText = data?.message || data?.error || "Request failed.";
    throw new Error(messageText);
  }

  return {
    answer: typeof data.answer === "string" ? data.answer : data.response || "",
    citations: Array.isArray(data.citations) ? data.citations : [],
    // Items the server named in the prompt: used to link mentions the model
    // left as plain text, so every item referred to ends up linked.
    links: Array.isArray(data.links) ? data.links : [],
    // Present only when the REST request came from a chatbot manager.
    debug: data.debug && typeof data.debug === "object" ? data.debug : null,
    disclaimer: typeof data.disclaimer === "string" ? data.disclaimer : "",
  };
}

// Admin-only retrieval trace. The server omits `debug` for visitors, so this
// prints nothing for them. Answers the question the Sources list can't: what
// did retrieval actually return, and where did a given document rank?
function logDebug(question, resp) {
  const debug = resp && resp.debug;
  if (!debug || typeof console === "undefined") return;

  const retrieval = Array.isArray(debug.retrieval) ? debug.retrieval : [];
  const cited = Array.isArray(debug.cited) ? debug.cited : [];
  const label =
    "Chatbot · " +
    (debug.cached ? "cached · " : "") +
    (debug.retrieved === null || typeof debug.retrieved === "undefined"
      ? "retrieved n/a"
      : debug.retrieved + " retrieved") +
    " · " +
    cited.length +
    " cited" +
    (debug.declined ? " · DECLINED (" + debug.declined + ")" : "");

  const group = console.groupCollapsed || console.group || console.log;
  group.call(console, label);
  console.log("question:", question);
  if (debug.model) console.log("model:", debug.model);

  if (retrieval.length && console.table) {
    console.log("retrieved chunks, best match first:");
    console.table(retrieval, ["rank", "score", "pdf", "title", "filename"]);
  } else if (!retrieval.length) {
    console.log(
      debug.cached
        ? "retrieval: not recorded (answer served from cache)"
        : "retrieval: nothing came back — the index has no chunk matching this question"
    );
  }

  if (cited.length && console.table) {
    console.log("of those, the answer cited:");
    console.table(cited, ["n", "pdf", "title", "url"]);
  } else if (!cited.length) {
    console.log("cited: none — the model used no citation annotations");
  }

  if (console.groupEnd) console.groupEnd();
}

function App({ restConfig }) {
  const welcomeMessage =
    (restConfig && restConfig.welcomeMessage) ||
    "Greetings! Can I answer any questions for you?";

  const welcomeResetMs =
    (restConfig && typeof restConfig.welcomeResetHours === "number"
      ? restConfig.welcomeResetHours
      : 24) *
    60 *
    60 *
    1000;

  const disclaimer = (restConfig && restConfig.disclaimer) || "";

  let defaultChatDialog = [
    {
      from: "chatbot",
      text:
        (restConfig && restConfig.introMessage) ||
        "Greetings - I’m an AI Chatbot that answers questions based on our published research & insights.",
    },
  ];
  // Second bubble: a flat disclaimer note (not styled as a message).
  if (disclaimer) {
    defaultChatDialog.push({ from: "disclaimer", text: disclaimer });
  }

  document.querySelectorAll(".sigil-bot-button a").forEach((element) => {
    element.onclick = function (e) {
      e.preventDefault();

      let prompt = this.getAttribute("href");
      prompt = prompt.substring(1);

      if (prompt) {
        askQuestion(prompt);
      } else {
        setChatDialogIsActive(true);
      }
    };
  });

  let defaultPreloadedQuestions =
    restConfig && Array.isArray(restConfig.initialQuestions) && restConfig.initialQuestions.length
      ? restConfig.initialQuestions
      : [
          "What does the research say about emergency savings?",
          "Tell me about inclusive investing.",
        ];

  const [chatDialog, setChatDialog] = useState(defaultChatDialog);
  const [preloadedQuestionsArr, setPreloadedQuestionsArr] = useState(
    defaultPreloadedQuestions,
  );
  const [isThinking, setIsThinking] = useState(false);
  const [chatDialogIsActive, setChatDialogIsActive] = useState(false);

  const handleMinimizeAll = () => {
    markWelcomeSeen();
    setChatDialogIsActive(false);
  };

  const handleOffClick = (e) => {
    e.stopPropagation();
    setChatDialogIsActive(false);
  };

  const askQuestion = async (prompt) => {
    if (!prompt || isThinking || preloadedQuestionsArr.length <= 0) return;

    setIsThinking(true);
    setPreloadedQuestionsArr([]);

    if (chatDialogIsActive !== true) {
      setChatDialogIsActive(true);
    }
    setChatDialog([...chatDialog, { from: "user", text: prompt }]);

    let resp;
    try {
      resp = await sendPromptToApi(prompt, restConfig);
    } catch (error) {
      setIsThinking(false);
      sendHeartbeat(restConfig, "error", error?.message || "chat request failed");
      setChatDialog([
        ...chatDialog,
        { from: "user", text: prompt },
        {
          from: "chatbot",
          text: "<pre>There was an error processing your request.</pre>",
        },
      ]);
      return;
    }
    setIsThinking(false);
    logDebug(prompt, resp);

    setChatDialog([
      ...chatDialog,
      { from: "user", text: prompt },
      {
        from: "chatbot",
        text: "<pre>" + (resp.answer || "") + "</pre>",
        citations: resp.citations,
        links: resp.links,
      },
    ]);

    let suggested;
    try {
      suggested = await sendPromptToApi(
        "Based on Commonwealth's research and the previous question `" +
          prompt +
          "`, suggest 2 short follow-up questions (each under 40 characters). Reply with the questions only, separated by '|||'.",
        restConfig,
        true, // internal — not logged to chat history
      );
    } catch (error) {
      return;
    }
    if (suggested && typeof suggested.answer === "string") {
      setPreloadedQuestionsArr(suggested.answer.split("|||"));
    }
  };

  useEffect(() => {
    if (shouldShowWelcome(welcomeResetMs)) {
      setTimeout(() => {
        if (chatDialogIsActive === false) {
          setChatDialogIsActive("half-minimized");
        }
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="Sigil-Bot-App-Wrapper" onClick={handleOffClick}>
      <div
        className="sigil-content-wrapper"
        onClick={(e) => e.stopPropagation()}
      >
        <ChatDialog
          isActive={chatDialogIsActive}
          isThinking={isThinking}
          askQuestion={askQuestion}
          chatDialog={chatDialog}
        />
        <PreloadedQuestions
          isActive={chatDialogIsActive}
          isThinking={isThinking}
          minimizeAll={handleMinimizeAll}
          askQuestion={askQuestion}
          questions={preloadedQuestionsArr}
          welcome={welcomeMessage}
        />
        <PersonaButton
          isActive={chatDialogIsActive}
          isThinking={isThinking}
          iconUrl={restConfig && restConfig.buttonIconUrl}
          onClick={() =>
            setChatDialogIsActive(
              handleChatDialogActiveState(chatDialogIsActive),
            )
          }
        />
      </div>
    </div>
  );
}

function handleChatDialogActiveState(chatDialogIsActive) {
  markWelcomeSeen();

  let returnVal;
  if (chatDialogIsActive === "half-minimized") {
    returnVal = true;
  } else {
    returnVal = !chatDialogIsActive;
  }
  return returnVal;
}

export default App;
